import { useState, useEffect, useRef, useCallback } from 'react';

interface UsePullToRefreshOptions {
  onRefresh: () => Promise<void>;
  threshold?: number; // Distance to pull before triggering refresh (default: 80px)
  enabled?: boolean; // Enable/disable pull-to-refresh (default: true)
  resistance?: number; // How much resistance during pull (default: 2.5)
}

interface PullToRefreshState {
  pulling: boolean;
  pullDistance: number;
  refreshing: boolean;
}

export const usePullToRefresh = ({
  onRefresh,
  threshold = 80,
  enabled = true,
  resistance = 2.5
}: UsePullToRefreshOptions) => {
  const [state, setState] = useState<PullToRefreshState>({
    pulling: false,
    pullDistance: 0,
    refreshing: false
  });

  const touchStartY = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  // True when the touch originated inside a scrollable child (e.g. an activity
  // list). We must not intercept those gestures so the child can scroll freely.
  const touchInScrollableChild = useRef<boolean>(false);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      touchInScrollableChild.current = false;

      if (!enabled || state.refreshing) return;

      const container = containerRef.current;
      if (!container) return;

      // Walk up from the touch target. If any element between the target and
      // the outer container is independently scrollable, let it handle the
      // gesture natively and skip pull-to-refresh entirely.
      let node = e.target as HTMLElement | null;
      while (node && node !== container) {
        const overflowY = window.getComputedStyle(node).overflowY;
        if (
          (overflowY === 'auto' || overflowY === 'scroll') &&
          node.scrollHeight > node.clientHeight
        ) {
          touchInScrollableChild.current = true;
          return;
        }
        node = node.parentElement;
      }

      // Only begin a pull gesture when the outer container is at the top
      if (container.scrollTop === 0) {
        touchStartY.current = e.touches[0].clientY;
      }
    },
    [enabled, state.refreshing]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (
        !enabled ||
        state.refreshing ||
        touchStartY.current === 0 ||
        touchInScrollableChild.current
      )
        return;

      const container = containerRef.current;
      if (!container || container.scrollTop > 0) return;

      const currentY = e.touches[0].clientY;
      const distance = currentY - touchStartY.current;

      if (distance > 0) {
        // Prevent default scrolling when pulling down
        e.preventDefault();

        const adjustedDistance = distance / resistance;
        setState({
          pulling: true,
          pullDistance: Math.min(adjustedDistance, threshold * 1.5),
          refreshing: false
        });
      }
    },
    [enabled, resistance, threshold, state.refreshing]
  );

  const handleTouchEnd = useCallback(async () => {
    if (!enabled || state.refreshing) return;

    if (state.pulling && state.pullDistance >= threshold) {
      // Trigger refresh
      setState({
        pulling: false,
        pullDistance: 0,
        refreshing: true
      });

      try {
        await onRefresh();
      } finally {
        setState({
          pulling: false,
          pullDistance: 0,
          refreshing: false
        });
      }
    } else {
      // Reset state
      setState({
        pulling: false,
        pullDistance: 0,
        refreshing: false
      });
    }

    touchStartY.current = 0;
  }, [
    enabled,
    onRefresh,
    state.pulling,
    state.pullDistance,
    state.refreshing,
    threshold
  ]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;

    container.addEventListener('touchstart', handleTouchStart, {
      passive: true
    });
    container.addEventListener('touchmove', handleTouchMove, {
      passive: false
    });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, handleTouchStart, handleTouchMove, handleTouchEnd]);

  return {
    containerRef,
    ...state,
    shouldRefresh: state.pullDistance >= threshold
  };
};
